import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';

import { format } from 'date-fns';

import { useBookingsContext } from '../contexts/BookingsContext';
import {
  initStrokes,
  mergePreserve,
  renderTranscriptPart,
} from '../utils/helpers';
import {
  IImage,
  ISession,
  ITranscript,
  TranscribeStateType,
  ITranscriptPart,
  IChat,
  ITranscriptSegment,
  ITranscriptSentence,
  ICanvas,
  IBackgroundSettings,
  IAttachment,
  INote,
} from '../global/interfaces';
import { IDelta, IBooking } from '@homeporter/types';
import { DailyParticipant } from '@daily-co/daily-js';

const initialize = (name: string) => {
  return name
    ?.split(' ')
    .slice(0, 2)
    .map((name: string) => name.charAt(0).toUpperCase())
    .join('');
};

export const IMAGES_BASE_URL =
  window._env_?.IMAGES_BASE_URL || process.env.REACT_APP_IMAGES_BASE_URL;

const defaultState: ISession = {
  setSession: (): void => {},
  resetSession: (): void => {},
  setSessionTranscript: (): void => {},
  resetSessionTranscript: (): void => {},

  localParticipantId: '',
  setLocalParticipantId: (): void => {},

  client: null,
  inspector: null,

  booking: undefined,
  setBooking: (_: IBooking | undefined): void => {},

  images: [],
  setImages: (): void => {},

  transcriptParts: [],
  setTranscriptParts: (): void => {},
  appendImage: (): void => {},

  chatHistory: [],
  setChatHistory: (): void => {},

  hasUnreadChat: false,
  setHasUnreadChat: () => {},

  transcriptSegments: [],
  setTranscriptSegments: () => {},
  transcribeState: 'off',
  setTranscribeState: () => {},

  canvasSketchSize: { width: 0, height: 0 },
  setCanvasSketchSize: () => {},

  isImageLoading: false,
  setIsImageLoading: () => {},

  hasUpdatedImages: false,
  setHasUpdatedImages: () => {},

  imagesMapRef: { current: null },
  replaceImageUrl: (_: IImage): IImage => _,

  selectedImage: null,
  setSelectedImage: () => {},

  selectedImageElement: null,
  setSelectedImageElement: () => {},

  inspectorPlaceholderText: '',
  canvasDisplayRef: null,

  strokesLayer: 0,
  setStrokesLayer: () => {},

  zoom: 1,
  setZoom: (): void => {},

  backgroundSettings: { type: '', index: -1 },
  setBackgroundSettings: () => {},

  remoteParticipant: undefined,
  setRemoteParticipant: () => {},

  agentParticipantId: '',
  setAgentParticipantId: () => {},

  sipSessionId: '',
  setSipSessionId: () => {},

  transcriptAttachments: {},
};

const SessionContext = createContext(defaultState);

export function SessionProvider({ children }: { children: JSX.Element }) {
  const [localParticipantId, setLocalParticipantId] = useState<string>('');
  const [booking, setBooking] = useState<IBooking | undefined>();
  const [client, setClient] = useState<any>();
  const [inspector, setInspector] = useState<any>();
  const [transcriptParts, setTranscriptParts] = useState<ITranscriptPart[]>([]);
  const [chatHistory, setChatHistory] = useState<IChat[]>([]);
  const [hasUnreadChat, setHasUnreadChat] = useState<boolean>(false);
  const [transcribeState, setTranscribeState] =
    useState<TranscribeStateType>('off');
  const [transcriptSegments, setTranscriptSegments] = useState<
    ITranscriptSegment[]
  >([]);

  const [canvasSketchSize, setCanvasSketchSize] = useState<ICanvas>({
    width: 0,
    height: 0,
  });

  const imagesMapRef = useRef<Map<string, IImage>>(new Map());

  const [images, setImages] = useState<string[]>([]);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [hasUpdatedImages, setHasUpdatedImages] = useState<boolean>(false);
  const [zoom, setZoom] = useState<number>(1);
  const [backgroundSettings, setBackgroundSettings] =
    useState<IBackgroundSettings>({ type: '', index: -1 });
  const [remoteParticipant, setRemoteParticipant] = useState<
    DailyParticipant | undefined
  >();
  const [agentParticipantId, setAgentParticipantId] = useState<string>('');
  const [sipSessionId, setSipSessionId] = useState<string>('');

  const { selectedBooking } = useBookingsContext();

  const transcriptAttachmentsRef = useRef<Record<string, (IDelta | INote)[]>>(
    {},
  );

  const setSession = (booking: IBooking | undefined, data: any) => {
    resetSession();
    console.log('::::inside setSession...');

    if (
      !booking ||
      !selectedBooking ||
      booking.booking_id !== selectedBooking.booking_id ||
      !data
    ) {
      return;
    }

    const {
      client,
      inspector,
      session_transcripts: session_transcript,
      session_chats,
      search_data,
      attachments,
    } = data;

    setClient(client);
    setInspector(inspector);
    //setCachedSearchData(search_data)
    setSessionTranscript(booking, session_transcript);
    setChatHistory(session_chats);
    setBooking(booking);
    setAttachments(attachments);
  };

  const resetSession = () => {
    console.log('::::reset session data for booking...');

    setLocalParticipantId('');

    setClient(undefined);
    setInspector(undefined);
    setTranscriptParts([]);
    setChatHistory([]);
    setHasUnreadChat(false);

    setTranscriptSegments([]);

    setCanvasSketchSize({ width: 0, height: 0 });

    setImages([]);
    setIsImageLoading(false);
    setHasUpdatedImages(false);
    setZoom(1);
    setBackgroundSettings({ type: '', index: -1 });

    setSelectedImage(null);
    setSelectedImageElement(null);

    setStrokesLayer(0);
    imagesMapRef.current = new Map();

    setBooking(undefined);
    transcriptAttachmentsRef.current = {};
  };

  const replaceImageUrl = (image: IImage) => {
    if (!image.src || !image.thumb.src) return image;

    image.src = `${IMAGES_BASE_URL}/${(image.src || '')
      .replace('https://vhc-pilot-v2.s3.amazonaws.com/', '')
      .replace(IMAGES_BASE_URL || '', '')
      .replace('vault/', '')}?ts=${Date.now()}`;

    image.thumb.src = `${IMAGES_BASE_URL}/${(image.thumb.src || '')
      .replace('https://vhc-pilot-v2.s3.amazonaws.com/', '')
      .replace(IMAGES_BASE_URL || '', '')
      .replace('vault/', '')}?ts=${Date.now()}`;

    return image;
  };

  const _appendImage = (image: IImage) => {
    if (!image.id) return;

    image.src = `${IMAGES_BASE_URL}/${image.src
      .replace('http://localhost:8080/', '')
      .replace('vault/', '')}?ts=${Date.now()}`;

    image.thumb.src = `${IMAGES_BASE_URL}/${image.thumb.src
      .replace('http://localhost:8080/', '')
      .replace('https://vhc-pilot-v2.s3.amazonaws.com/', '')
      .replace('vault/', '')}?ts=${Date.now()}`;

    imagesMapRef.current.set(image.id, image);
  };

  const appendImage = (image: IImage) => {
    if (!image?.id) return;

    _appendImage(image);
    setImages((prev: string[]) => {
      const id = image.id;
      if (id) {
        return [...prev, id];
      }
      return prev;
    });
    setTranscriptParts((prev: ITranscriptPart[]) => [...prev, image]);
  };

  const setSessionTranscript = (booking: IBooking, session_transcript: any) => {
    if (!session_transcript) {
      setTranscriptParts([]);
      setImages([]);
      return;
    }
    console.log('::::setSessionTranscript:', session_transcript);

    booking.transcript_v2_exists = session_transcript?.length > 0;

    const duplicates: any = {};
    const filteredDuplicatesTranscriptParts = session_transcript.filter(
      (item: ITranscriptPart, index: number, array: ITranscriptPart[]) => {
        item.time = new Date(
          (item as ITranscript)?.timestamp || (item as IImage)?.date || 0,
        ).getTime();

        const str = JSON.stringify(item);
        const isDuplicate = duplicates[str];
        duplicates[str] = true;

        return !isDuplicate;
      },
    );

    const resortedTranscriptParts = filteredDuplicatesTranscriptParts.sort(
      (a: ITranscriptPart, b: ITranscriptPart) => {
        return (a.time || 0) - (b.time || 0);
      },
    );

    const imagesMap = imagesMapRef.current;
    const images: string[] = [];
    //const transcriptParts = resortedTranscriptParts.filter((item: any) => {
    const transcriptParts = session_transcript.filter((item: any) => {
      if (item?.type === 'image') {
        if (!item.id) return;
        if (imagesMap.has(item.id)) {
          mergePreserve(imagesMap.get(item.id), item);
          return;
        }

        initStrokes(item);
        _appendImage(item as IImage);

        images.push(item.id);
      }

      return item;
    });

    console.log('::::images:', images);

    setTranscriptParts(transcriptParts);
    setImages(images);
  };

  const setAttachments = (attachments: IAttachment[]) => {
    console.log('::::setAttachments:', attachments);
    if (!attachments) return;

    attachments.forEach((attachment) => {
      if (attachment.delta) {
        transcriptAttachmentsRef.current[attachment.id] = [attachment.delta];
      } else if (attachment.note) {
        const list = (transcriptAttachmentsRef.current[attachment.id] ??= []);
        while (list.length <= attachment.note.idx) {
          list.push({
            idx: list.length,
            title: '',
            description: '',
            src: '',
          });
        }

        list[attachment.note.idx] = attachment.note;
      }
    });
  };

  const resetSessionTranscript = () => {
    setTranscriptSegments([]);
  };

  /* move to copilotContext...
  const setCachedSearchData = (cached_search_data: ISearchResults[]) => {//these are the cached semantic search results. Need a way to reset these results
    if (!cached_search_data) return

    console.log('::::cached_search_data:', cached_search_data)

    const documentParagraphQueue:IAIScore[] = []
    cached_search_data.forEach((aiResults:ISearchResults) => {
      transcriptSearchResultsMapRef.current.set(aiResults.sid, aiResults)

      aiResults.scores.forEach((score:IAIScore) => {
        if (!documentParagraphMapRef.current.has(`${score.did}-${score.idx}`)) {
          documentParagraphQueue.push(score)
          documentParagraphMapRef.current.set(`${score.did}-${score.idx}`, {
            did: score.did,
            idx: score.idx,
            ref: {},
            text: ''
          })
        }
      })
    })

    if (documentParagraphQueue.length > 0) {
      console.log('::::documentParagraphQueue:', documentParagraphQueue)
      setDocumentParagraphQueue(documentParagraphQueue)
    } 
  }
*/

  const processTranscriptSegments = (
    transcriptParts: ITranscriptPart[],
    previousSegment: ITranscriptSegment,
    previousSegments: ITranscriptSegment[],
  ): ITranscriptSegment[] => {
    const transcriptSegments: ITranscriptSegment[] = transcriptParts.reduce(
      (
        acc: ITranscriptSegment[],
        currentPart: ITranscriptPart,
        index: number,
      ) => {
        if (currentPart?._processed) return acc;

        currentPart._processed = true;

        if ((currentPart as ITranscript).text) {
          const previousPart = index > 0 ? transcriptParts[index - 1] : null;
          const currentTextPart = currentPart as ITranscript;

          if (!currentPart?.time)
            currentPart.time = !currentTextPart.timestamp
              ? 0
              : new Date(currentTextPart.timestamp).getTime();

          if (
            !previousSegment.user_id ||
            previousSegment.user_id !== currentTextPart.user_id ||
            currentPart.time -
              (previousPart?.time || previousSegment.time || 0) >
              5 * 1000 //60 second gap with last transcript part
          ) {
            previousSegment = renderTranscriptPart(currentPart, index);

            acc.push(previousSegment);
          } else {
            let lastSentence: ITranscriptSentence | undefined = previousSegment
              ?.sentences?.length
              ? previousSegment.sentences[previousSegment.sentences.length - 1]
              : undefined;
            if (
              !lastSentence?.text ||
              lastSentence.text.endsWith('.') ||
              lastSentence.text.endsWith('?') ||
              lastSentence.text.endsWith('!')
            ) {
              lastSentence = {
                sid: `${index}`,
                text: currentTextPart.text as string,
              };

              if (!previousSegment?.sentences) previousSegment.sentences = [];
              previousSegment.sentences.push(lastSentence);
            } else {
              lastSentence.text = `${lastSentence.text} ${currentTextPart.text}`;
            }
          }
        } else if ((currentPart as IImage).src) {
          previousSegment = renderTranscriptPart(currentPart, index);
          acc.push(previousSegment);
        }

        return acc;
      },
      previousSegments,
    );

    let refDate: Date | null = null;
    transcriptSegments.forEach((segment, index) => {
      if (segment.image_id) return;

      if (!refDate) refDate = new Date(segment.timeStamp);
      const delta = Math.round((segment.time - refDate.getTime()) / 1000);

      const hours = Math.floor(delta / 3600);
      const minutes = Math.floor(delta / 60) - hours * 60;
      const paddedMinutes = String(minutes).padStart(2, '0');
      const seconds = delta % 60;
      const paddedSeconds = String(seconds).padStart(2, '0');

      if (hours > 3 || delta <= 0) {
        refDate = new Date(segment.timeStamp);
        segment.displayTime = format(refDate, 'yyyy-MM-dd HH:mm:ss');
      } else {
        //segment.displayTime = `${hours ? hours + 'hr ' : ''}${
        //  minutes ? minutes + 'min ' : ''
        //}${seconds}sec`;

        segment.displayTime = `${hours ? hours + ':' : ''}${paddedMinutes}:${paddedSeconds}`;
      }
    });

    return transcriptSegments;
  };

  useEffect(() => {
    if (!transcriptParts || !transcriptParts.length) return;

    let previousSegment: ITranscriptSegment = !transcriptSegments.length
      ? { timeStamp: '', time: 0 }
      : transcriptSegments[transcriptSegments.length - 1];

    const _transcriptSegments: ITranscriptSegment[] = processTranscriptSegments(
      transcriptParts,
      previousSegment,
      transcriptSegments,
    );

    console.log('setTranscriptSegments:', _transcriptSegments);
    setTranscriptSegments([..._transcriptSegments]);
  }, [transcriptParts]);

  const [selectedImage, setSelectedImage] = useState<IImage | null>(null);
  const [selectedImageElement, setSelectedImageElement] =
    useState<HTMLImageElement | null>(null);

  const [strokesLayer, setStrokesLayer] = useState<number>(0);

  const inspectorPlaceholderText = initialize(booking?.inspector?.name);
  const canvasDisplayRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!selectedImage) {
      setSelectedImageElement(null);
      return;
    }

    if (selectedImage.src)
      console.log(`setSelectedImage: ${selectedImage.src}`);

    setIsImageLoading(true);

    var img = new Image();
    img.onload = () => {
      console.log(
        `::::image is loaded img.width: ${img.width} x  img.height: ${img.height}`,
      );
      setIsImageLoading(false);
      setSelectedImageElement(img);
    };

    img.onerror = () => {
      setIsImageLoading(false);
      alert('Error occured while loading selected image !');
    };

    setStrokesLayer(!selectedImage.layer ? 0 : selectedImage.layer);
    if (selectedImage.src) img.src = selectedImage.src;
    else {
      setIsImageLoading(false);
      img.width = selectedImage.width;
      img.height = selectedImage.height;
    }
  }, [selectedImage]);
  /*
  useEffect(() => {
    const repeatingFunction = () => {
      console.log(
        '::::Session context repeatFunction called at:',
        new Date().toISOString(),
      );
    };

    repeatingFunction();
    const intervalId = setInterval(repeatingFunction, 2000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);
*/

  return (
    <SessionContext.Provider
      value={{
        inspectorPlaceholderText,

        setSession,
        resetSession,
        setSessionTranscript,
        resetSessionTranscript,

        localParticipantId,
        setLocalParticipantId,

        client,
        inspector,

        booking,
        setBooking,

        images,
        setImages,

        transcriptParts,
        setTranscriptParts,
        appendImage,

        chatHistory,
        setChatHistory,

        hasUnreadChat,
        setHasUnreadChat,

        hasUpdatedImages,
        setHasUpdatedImages,

        transcriptSegments,
        setTranscriptSegments,
        transcribeState,
        setTranscribeState,

        canvasSketchSize,
        setCanvasSketchSize,

        imagesMapRef,
        replaceImageUrl,

        selectedImage,
        setSelectedImage,

        selectedImageElement,
        setSelectedImageElement,

        isImageLoading,
        setIsImageLoading,

        strokesLayer,
        setStrokesLayer,

        canvasDisplayRef,

        zoom,
        setZoom,

        backgroundSettings,
        setBackgroundSettings,

        remoteParticipant,
        setRemoteParticipant,
        agentParticipantId,
        setAgentParticipantId,
        sipSessionId,
        setSipSessionId,

        transcriptAttachments: transcriptAttachmentsRef.current,
      }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSessionContext() {
  return useContext(SessionContext);
}
